import { ProjectOverviewDto, ProjectWithAssigneesDto } from '../../commonTypes/acs/project.types';
import { ProjectAssigneeRateRowDto } from '../../commonTypes/project.types';
import { formatSecondsAsHhMmSs } from './formatSecondsAsHhMmSs';
import { RateBucketKey, RateBucketSeconds, isUnmatchedBucketKey, userSubFromUnmatchedKey } from './attributeTimelogsToAssigneeRateRows';

export type ProjectTimesheetTableRow = {
    rowKey: string;
    userSub: string;
    member: string;
    role: string;
    hourlyRate: number | null;
    totalUnsubmitted: string;
    totalSubmitted: string;
    imageUrl?: string;
};

function getUserDisplay(
    userSub: string,
    overviewByUserSub: Map<string, ProjectOverviewDto>,
    project: ProjectWithAssigneesDto
): { member: string; role: string; imageUrl?: string } {
    const ov = overviewByUserSub.get(userSub);
    const assignee = project.projectAssignees?.find(a => a.userSub === userSub);
    const member = ov?.member || [assignee?.givenName, assignee?.familyName].filter(Boolean).join(' ').trim() || 'Unknown';
    const role = (ov?.title || ov?.role || assignee?.title || '').trim();
    return { member, role, imageUrl: ov?.imageUrl ?? assignee?.imageUrl };
}

export function buildProjectTimesheetRowsFromRateAttribution(
    rateRows: ProjectAssigneeRateRowDto[],
    projectId: string,
    buckets: Map<RateBucketKey, RateBucketSeconds>,
    overviewByUserSub: Map<string, ProjectOverviewDto>,
    project: ProjectWithAssigneesDto,
    options?: { onlyUserSub?: string }
): ProjectTimesheetTableRow[] {
    const only = options?.onlyUserSub;
    const filteredRates = rateRows.filter(r => r.projectId === projectId && (!only || r.userSub === only));

    const rows: ProjectTimesheetTableRow[] = [];

    for (const r of filteredRates) {
        const b = buckets.get(r.id) ?? { submitted: 0, unsubmitted: 0 };
        if (b.submitted === 0 && b.unsubmitted === 0) continue;
        const { member, role, imageUrl } = getUserDisplay(r.userSub, overviewByUserSub, project);
        rows.push({
            rowKey: r.id,
            userSub: r.userSub,
            member,
            role,
            hourlyRate: r.rate,
            totalUnsubmitted: formatSecondsAsHhMmSs(b.unsubmitted),
            totalSubmitted: formatSecondsAsHhMmSs(b.submitted),
            imageUrl,
        });
    }

    for (const [key, b] of buckets.entries()) {
        if (!isUnmatchedBucketKey(key)) continue;
        if (b.submitted === 0 && b.unsubmitted === 0) continue;
        const userSub = userSubFromUnmatchedKey(key);
        if (only && userSub !== only) continue;
        const { member, role, imageUrl } = getUserDisplay(userSub, overviewByUserSub, project);
        rows.push({
            rowKey: key,
            userSub,
            member,
            role,
            hourlyRate: null,
            totalUnsubmitted: formatSecondsAsHhMmSs(b.unsubmitted),
            totalSubmitted: formatSecondsAsHhMmSs(b.submitted),
            imageUrl,
        });
    }

    const usersWithAnyAttributedRow = new Set<string>();
    for (const [key, b] of buckets.entries()) {
        if (b.submitted === 0 && b.unsubmitted === 0) continue;
        if (isUnmatchedBucketKey(key)) {
            usersWithAnyAttributedRow.add(userSubFromUnmatchedKey(key));
        } else {
            const rr = rateRows.find(x => x.id === key);
            if (rr) usersWithAnyAttributedRow.add(rr.userSub);
        }
    }

    for (const item of overviewByUserSub.values()) {
        if (only && item.userSub !== only) continue;
        if (usersWithAnyAttributedRow.has(item.userSub)) continue;
        if (item.submitted === 0 && item.unsubmitted === 0) continue;
        const assignee = project.projectAssignees?.find(a => a.userSub === item.userSub);
        const { member, role, imageUrl } = getUserDisplay(item.userSub, overviewByUserSub, project);
        rows.push({
            rowKey: `fallback__${item.userSub}`,
            userSub: item.userSub,
            member,
            role,
            hourlyRate: assignee?.rate ?? null,
            totalUnsubmitted: formatSecondsAsHhMmSs(item.unsubmitted),
            totalSubmitted: formatSecondsAsHhMmSs(item.submitted),
            imageUrl,
        });
    }

    return rows;
}
